<script setup>
  import {computed, inject, onMounted, ref} from "vue";

  import ComponentAccountInfoSupportCodeDialog from "@components/Dialogs/AccountInfoSupportCodeDialog.vue";
  import ComponentBmpImportDialog from "@components/Dialogs/BmpImportDialog/BmpImportDialog.vue";
  import ComponentDemodataDialog from "@components/Dialogs/DemodataDialog/DemodataDialog.vue";
  import ComponentJsonImportDialog from "@components/Dialogs/JsonImportDialog.vue";
  import ComponentMergeAccountInfoDialog from "@components/Dialogs/MergeAccountInfoDialog.vue";
  import AuthHeaderDropdown from "@components/Layout/Components/AuthHeader/Components/Dropdown/Dropdown.vue";
  import AuthHeaderDropdownItem from "@components/Layout/Components/AuthHeader/Components/Dropdown/DropdownItem.vue";
  import AuthHeaderNavItem from "@components/Layout/Components/AuthHeader/Components/NavItem.vue";
  import AuthHeaderNotificationsSlideover from "@components/Layout/Components/AuthHeader/Components/Notifications/NotificationsSlideover.vue";
  import Logo from "@components/Layout/Components/Logo.vue";
  import {usePage} from "@inertiajs/vue3";
  import {MedBadge, MedButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";
  import Auth from "@utils/Auth.js";

  defineOptions({
    name: "LayoutAuthHeader",
  });

  const page = usePage();

  const privacy = inject("$privacy");

  const refAuthHeaderNotificationsSlideover = ref(null);
  const refBmpImportDialog = ref(null);
  const refDemodataDialog = ref(null);
  const refJsonImportDialog = ref(null);
  const refMergeAccountInfoDialog = ref(null);
  const refAccountInfoSupportCodeDialog = ref(null);

  const navData = ref([]);
  const navDataUser = ref({});
  const showMobile = ref(false);

  const upgradeUrl = computed(() => {
    return import.meta.env.MC_PHARMA4U_UPGRADE_URL;
  });

  onMounted(() => {
    globalThis.addEventListener("breakpoint", () => {
      // any resize event should close the mobile menu
      if (showMobile.value) {
        showMobile.value = false;
      }
    });

    navData.value = [
      {
        title: "Neue Analyse",
        icon: "expand_more",
        show: page.props.can.create_records || page.props.can.view_patientinputs,
        testId: "auth-header-new-analysis",
        menuItems: [
          {
            label: "Manuelle Erfassung",
            description: "Datenerfassung per Hand für neuen Patienten",
            icon: "edit",
            show: page.props.can.create_records,
            href: route("patients.create") + "?continue-with-record",
            testId: "auth-header-dropdown-create-patient",
          },
          {
            label: "Import aus Medikationsplan",
            description: "Scan oder Upload des BMP",
            icon: "barcode_reader",
            href: null,
            clickEvent: () => refBmpImportDialog.value.open(),
            show: page.props.can.create_records,
            testId: "auth-header-dropdown-import-bmp",
          },
          {
            label: "Import aus JSON-Datei",
            icon: "description",
            description: "Zuvor exportierte Analyse importieren",
            href: null,
            clickEvent: () => refJsonImportDialog.value.open(),
            //TODO remove check for local
            show: page.props.can.json_import,
            testId: "auth-header-dropdown-import-json",
          },
          {
            label: "Fragebögen verwalten",
            description: "Arbeiten Sie Hand in Hand mit Patienten und Pflegefachkräften",
            icon: "note_alt",
            href: route("patientinput-client.index"),
            show: page.props.can.view_patientinputs,
            testId: "auth-header-dropdown-import-patient-input",
          },
        ],
      },
      {
        title: "Patienten",
        href: route("patients.index"),
        icon: "view_list",
        testId: "auth-header-dropdown-patient",
      },
      {
        title: "mehr",
        icon: "expand_more",
        testId: "auth-header-more",
        menuItems: [
          {
            label: "Kontakte",
            description: "Ärzte, Pflegedienste, Heime",
            icon: "local_hospital",
            href: route("contacts.index"),
            testId: "auth-header-dropdown-contact",
          },
          {
            label: "Beispielfälle",
            description: "Beispielfälle zum Testen",
            icon: "grade",
            href: null,
            clickEvent: () => refDemodataDialog.value.open(),
            show: page.props.can.create_records,
            testId: "auth-header-dropdown-demo-data",
          },
          {
            label: "Arbeitshilfen",
            description: "Hilfe und Support",
            icon: "help",
            href: route("documents.index"),
            testId: "auth-header-dropdown-documentation",
          },
          {
            label: "Hilfe pharma4u",
            description: "Hilfe und Support bei pharma4u",
            icon: "support",
            href: page.props.links.p4u + "/apotheker/medicheck/arbeitshilfen",
            isExternal: true,
            show: page.props?.client?.license?.is_pro ?? false,
            testId: "auth-header-dropdown-p4u-help",
          },
        ],
      },
    ];

    navDataUser.value = {
      icon: "person",
      testId: "auth-header-dropdown-user",
      menuItems: [
        {
          label: "Profil",
          description: "Profil bei pharma4u anpassen",
          icon: "person",
          href: page.props.links.p4u + "/allgemein/benutzerkonto",
          isExternal: true,
          show: page.props?.client?.license?.is_pro ?? false,
          testId: "auth-header-dropdown-profile",
        },
        {
          label: "Datenschutzpasswort",
          description: "Passwort für Verschlüsselung ändern",
          icon: "lock",
          href: route("privacyPassword.show", {user: privacy.getCurrentUser().id}),
          data: {user: privacy.getCurrentUser().id},
          show: !(page.props?.client?.license?.is_connect ?? false),
          testId: "auth-header-dropdown-privacy-password",
        },
        {
          label: "Einstellungen",
          icon: "settings",
          href: route("settings.show", {user: privacy.getCurrentUser().id}),
          data: {user: privacy.getCurrentUser().id},
          testId: "auth-header-dropdown-settings",
        },
        {
          label: "Accountinformationen",
          icon: "info",
          clickEvent: () => refAccountInfoSupportCodeDialog.value.open(),
          testId: "auth-header-dropdown-account-info",
        },
        {
          label: "Accounts verknüpfen",
          icon: "link",
          clickEvent: () => refMergeAccountInfoDialog.value.open(),
          testId: "auth-header-dropdown-merge-account",
        },
        {
          label: "Logout",
          icon: "power_settings_new",
          clickEvent: () => Auth.logout(privacy),
          canBeActive: true,
          as: "button",
          testId: "auth-header-dropdown-logout",
        },
      ],
    };
  });

  const handleMobileMenu = () => {
    showMobile.value = !showMobile.value;
  };

  const closeMenu = () => {
    if (showMobile.value) showMobile.value = false;
  };
</script>

<template>
  <header class="sticky top-0 z-50 bg-white shadow-md" v-bind="$attrs">
    <nav class="mx-auto flex h-16 max-w-11xl items-center justify-between px-2 md:px-4">
      <div class="flex items-center">
        <div class="flex shrink-0 items-center">
          <MedLink
            aria-label="Zum Dashboard"
            as="inertia"
            :href="$route('dashboard.index')"
            class="rounded-md"
            data-test="auth-header-medicheck-logo-inertia-link"
          >
            <Logo />
          </MedLink>
        </div>

        <div class="hidden md:block">
          <div class="hidden sm:ml-6 sm:flex sm:gap-4 lg:gap-8">
            <template v-for="(navItem, index) in navData.filter((i) => i.show ?? true)" :key="index">
              <template v-if="navItem.menuItems">
                <AuthHeaderDropdown :test-id="navItem.testId" alignment="left">
                  <template #headline>
                    <AuthHeaderNavItem :label="navItem.title" :icon="navItem.icon" :test-id="navItem.testId" />
                  </template>

                  <template #options>
                    <template v-for="(item, key) in navItem.menuItems.filter((i) => i.show ?? true)" :key="key">
                      <AuthHeaderDropdownItem
                        :label="item.label"
                        :description="item.description"
                        :icon="item.icon"
                        :href="item.href"
                        :is-external="item.isExternal"
                        :data="item.data"
                        :method="item.method"
                        :as="item.as"
                        :test-id="item.testId"
                        @click="item.clickEvent"
                      />
                    </template>
                  </template>
                </AuthHeaderDropdown>
              </template>

              <template v-else>
                <!-- no :icon on first level -->
                <AuthHeaderNavItem :label="navItem.title" :href="navItem.href" :test-id="navItem.testId" />
              </template>
            </template>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <template v-if="$page.props?.client?.license?.is_core ?? false">
          <MedTooltip content="Upgrade Info" side="bottom">
            <MedButton
              aria-label="Upgrade Info"
              as="a"
              :href="upgradeUrl"
              variant="primary"
              flat
              data-test="auth-header-upgrade-account-button"
              icon="stars"
              size="lg"
              rounded="full"
              icon-filled="false"
            />
          </MedTooltip>
        </template>

        <template v-if="$page.props?.client?.license?.is_pro ?? false">
          <MedTooltip
            :content="
              $page.props.allNotificationsCount === 0
                ? 'Sie haben keine Benachrichtigung'
                : `Sie haben ${$page.props.allNotificationsCount} Benachrichtigungen`
            "
            side="bottom"
          >
            <MedBadge max="99" :label="$page.props.allNotificationsCount" data-test="auth-header-notification-badge">
              <MedButton
                aria-label="Benachrichtigungen anzeigen"
                variant="ghost"
                data-test="auth-header-notification-button"
                icon="notifications"
                size="lg"
                rounded="full"
                @click="refAuthHeaderNotificationsSlideover.open()"
              />
            </MedBadge>
          </MedTooltip>
        </template>

        <MedButton
          aria-label="Hauptmenü öffnen"
          variant="ghost"
          data-test="auth-header-mobile-open-menu-button"
          :icon="showMobile ? 'close' : 'menu'"
          size="lg"
          rounded="full"
          class="flex md:hidden"
          @click="handleMobileMenu"
        />

        <AuthHeaderDropdown alignment="right" test-id="auth-header-user" class="hidden md:block">
          <template #headline>
            <MedTooltip content="Persönliche Einstellungen" side="bottom">
              <MedButton
                variant="ghost"
                data-test="auth-header-user-button"
                :icon="navDataUser.icon"
                size="lg"
                rounded="full"
              />
            </MedTooltip>
          </template>

          <template #options>
            <div class="items-top flex gap-2">
              <template v-if="privacy.getCurrentUser()?.name">
                <div class="flex items-center">
                  <span class="text-left font-semibold" data-test="auth-header-user-dropdown-username">
                    {{ privacy.getCurrentUser()?.name }}
                  </span>
                </div>
              </template>
            </div>

            <template v-for="(item, index) in navDataUser.menuItems.filter((i) => i.show ?? true)" :key="index">
              <template v-if="item.show ?? true">
                <AuthHeaderDropdownItem
                  :label="item.label"
                  :description="item.description"
                  :icon="item.icon"
                  :href="item.href"
                  :is-external="item.isExternal"
                  :data="item.data"
                  :method="item.method"
                  :as="item.as"
                  :test-id="item.testId"
                  @click="item.clickEvent"
                />
              </template>
            </template>
          </template>
        </AuthHeaderDropdown>
      </div>
    </nav>

    <template v-if="showMobile">
      <!-- Click handler closes mobile menu when clicking outside interactive elements (UX pattern) -->
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
      <nav class="absolute top-16 right-0 left-0 bg-white shadow-lg md:hidden" @click="closeMenu">
        <div class="min-h-full space-y-2 px-2 py-3 sm:px-3">
          <template v-for="(navItem, index) in navData.filter((i) => i.show ?? true)" :key="index">
            <!-- handle clickable first level items as if they are dropdown items -->
            <template v-if="!navItem.menuItems">
              <AuthHeaderDropdownItem
                :label="navItem.title"
                :icon="navItem.icon"
                :href="navItem.href"
                :test-id="navItem.testId"
              />
            </template>

            <template v-if="navItem.menuItems">
              <template v-for="(item, key) in navItem.menuItems.filter((i) => i.show ?? true)" :key="key">
                <AuthHeaderDropdownItem
                  :label="item.label"
                  :description="item.description"
                  :icon="item.icon"
                  :href="item.href"
                  :is-external="item.isExternal"
                  :data="item.data"
                  :method="item.method"
                  :as="item.as"
                  :test-id="item.testId"
                  @click="item.clickEvent"
                />
              </template>
            </template>
          </template>
        </div>

        <div class="border-t border-gray-300 px-2 pt-4 pb-3 sm:px-3">
          <template v-if="privacy.getCurrentUser()?.name">
            <div class="mb-3 ml-3 font-semibold" data-test="auth-header-mobile-dropdown-user">
              {{ privacy.getCurrentUser()?.name }}
            </div>
          </template>

          <div class="space-y-1">
            <template v-for="(item, index) in navDataUser.menuItems.filter((i) => i.show ?? true)" :key="index">
              <template v-if="item.show ?? true">
                <AuthHeaderDropdownItem
                  :label="item.label"
                  :description="item.description"
                  :icon="item.icon"
                  :href="item.href"
                  :is-external="item.isExternal"
                  :data="item.data"
                  :method="item.method"
                  :as="item.as"
                  :test-id="item.testId"
                  @click="item.clickEvent"
                />
              </template>
            </template>
          </div>
        </div>
      </nav>
    </template>

    <AuthHeaderNotificationsSlideover ref="refAuthHeaderNotificationsSlideover" test-id="auth-header" />
  </header>

  <ComponentBmpImportDialog ref="refBmpImportDialog" />
  <ComponentJsonImportDialog ref="refJsonImportDialog" />
  <ComponentDemodataDialog ref="refDemodataDialog" />

  <ComponentMergeAccountInfoDialog ref="refMergeAccountInfoDialog" test-id="auth-header" />
  <ComponentAccountInfoSupportCodeDialog ref="refAccountInfoSupportCodeDialog" test-id="auth-header" />
</template>
